import React from 'react';
import styled from 'styled-components';
import { theme } from './theme';
import { useTranslation } from 'react-i18next';
import Header from './components/Header';
import { XROAD_PAGES } from './constants';
import bgImage from './assets/graphics/bg.jpg';
import {
  BaseInner,
  BaseFormTitle,
  BaseAccountTypes,
  BaseGrid,
  BaseCard,
  BaseButton,
  BaseFeatureList,
} from './components/shared/BaseAccountSelector';

// Xroad-specific styled components
// XroadContainer - full viewport height since control bar is hidden on Xroad page
const XroadContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100svh;
  color: ${theme.colors.white};
  display: flex;
  justify-content: center;
  padding: 0 0 ${theme.spacing.lg} 0;
  margin: 0;
  box-sizing: border-box;
  background-image: url(${bgImage});
  background-size: cover;
  background-position: center;
  overflow-y: auto;
  overflow-x: hidden;
`;

const TitleHeader = styled.div`
  font-weight: ${theme.fontWeights.bold};
`;

const CardHeader = styled.div`
  border-bottom: 1px solid ${theme.colors.goldAlt};
`;

const CardTitle = styled.p`
  width: 100%;
  font-size: ${theme.fontSizes.xl};
  font-weight: ${theme.fontWeights.bold};
  color: ${theme.colors.gold};
  text-align: center;
  margin-bottom: 0.25rem;
`;

const FooterLink = styled.div`
  text-align: center;
  font-size: ${theme.fontSizes.xl};
`;

/**
 * Props pro Xroad komponentu
 */
interface XroadProps {
  /** Callback volaný při výběru stránky */
  onSelectPage: (pageId: number) => void;
}

/**
 * Struktura pro jednu možnost na rozcestníku
 */
interface XroadOption {
  id: number;
  optionKey: string;
}

const optionsData: readonly XroadOption[] = [
  {
    id: XROAD_PAGES.CONTINUE,
    optionKey: 'fromStart',
  },
  {
    id: XROAD_PAGES.INTRO_ACCOUNTS,
    optionKey: 'introAccounts',
  },
  {
    id: XROAD_PAGES.OPEN_ACCOUNT,
    optionKey: 'openAccount',
  }
] as const;

/**
 * Komponenta rozcestníku - umožňuje uživateli vybrat si cestu prezentací
 */
export default function Xroad({ onSelectPage }: XroadProps): React.ReactElement {
  const { t } = useTranslation('common');

  return (
    <XroadContainer>
      <BaseInner>
        <Header>
          <TitleHeader>{t('xroad.header')}</TitleHeader>
          <BaseFormTitle>{t('xroad.title')}</BaseFormTitle>
        </Header>

        <BaseAccountTypes>
          <BaseGrid>
            {optionsData.map((opt, i) => (
              <BaseCard key={i} onClick={() => onSelectPage(opt.id)}>
                <CardHeader>
                  <CardTitle>{t(`xroad.options.${opt.optionKey}.title`)}</CardTitle>
                </CardHeader>
                <BaseFeatureList>
                  {(t(`xroad.options.${opt.optionKey}.features`, { returnObjects: true }) as string[]).map((f, j) => (
                    <li key={j}>{f}</li>
                  ))}
                </BaseFeatureList>
                <BaseButton>{t('buttons.select')}</BaseButton>
              </BaseCard>
            ))}
          </BaseGrid>
        </BaseAccountTypes>
        <FooterLink>
          {t('footer.officialWebsite')} <a href='https://www.firegoldgroup.com' target='_blank' rel='noreferrer'>www.firegoldgroup.com</a>
        </FooterLink>
      </BaseInner>
    </XroadContainer>
  );
}
